import {
  AllocationPreviewApiResponse,
  AllocationPreviewRequest,
  AllocationPreviewRow,
  AltiusRes,
  CorrelationMatrixSettings,
  DisplayModeResponse,
  DriftSliceType,
  IObjectiveDefaults,
  IObjectiveDefaultsSaveRequest,
  IObjectiveSource,
  IObjectivesResponse,
  IObjectivesSaveRequest,
  IPortfolioConstructionCompareGraph,
  IPortfolioConstructionCorrelationData,
  IPortfolioConstructionCorrelationItem,
  IPortfolioConstructionDealTreeItem,
  IPortfolioConstructionPieChart,
  ObjectiveConstraintKey,
  PlannedDeal,
  PlannedDealApi,
  PlannedDealRequest,
  SliceGroup,
  SliceRequest,
  TargetPolicyItem,
  TargetPolicySaveRequest
} from "store/types";

import { ReportsAction } from "../actions";
import { AsyncActions, AsyncActionsParams } from "../base";

// Planned-deal monetary values travel as decimal strings on the wire; the UI works in numbers.
const toMoneyNumber = (v: string | number | null | undefined): number => {
  const n = typeof v === "number" ? v : parseFloat(v ?? "");
  return Number.isFinite(n) ? n : 0;
};

const mapPlannedDeal = (raw: PlannedDealApi): PlannedDeal => ({
  id: raw.id,
  name: raw.name,
  asset_class: raw.asset_class ?? null,
  sub_asset_class: raw.sub_asset_class ?? null,
  amount: toMoneyNumber(raw.amount),
  currency: raw.currency,
  expected_date: raw.expected_date,
  investors: (raw.investors ?? []).map((a) => ({
    investor_id: a.investor_id,
    investor_name: a.investor_name,
    allocated_amount: toMoneyNumber(a.allocated_amount),
    investor_not_found: Boolean(a.investor_not_found)
  }))
});

// Build the wire body, sending money as strings per the API contract.
// Custom asset classes are flat — never send sub_asset_class_id for them (the backend 422s).
const toPlannedDealBody = (data: PlannedDealRequest) => ({
  name: data.name,
  asset_class_id: data.asset_class_id,
  asset_class_type: data.asset_class_type,
  ...(data.asset_class_type === "system" && data.sub_asset_class_id != null
    ? { sub_asset_class_id: data.sub_asset_class_id }
    : {}),
  amount: String(data.amount),
  currency: data.currency,
  expected_date: data.expected_date,
  investors: data.investors.map((a) => ({
    investor_id: a.investor_id,
    allocated_amount: String(a.allocated_amount)
  }))
});

export interface IPortfolioConstructionActions {
  getPieChartDataAction: (
    sliceId: number | string,
    sliceType: "investors" | "slices"
  ) => Promise<IPortfolioConstructionPieChart[]>;
  getCompareGraphDataAction: (
    sliceId: number | string,
    sliceType: "investors" | "slices"
  ) => Promise<IPortfolioConstructionCompareGraph>;
  getCorrelationDataAction: (data: {
    investor_id?: number;
    dateRange: { from: string; to: string };
    items: IPortfolioConstructionCorrelationItem[];
  }) => Promise<IPortfolioConstructionCorrelationData>;

  getDealTreeAction: (investorId?: number) => Promise<IPortfolioConstructionDealTreeItem[]>;
  getDisplayModeAction: (
    sliceId: number | string,
    sliceType: DriftSliceType,
    currency?: string
  ) => Promise<DisplayModeResponse>;
  setCorrelationMatrixSettingsAction: (data: CorrelationMatrixSettings) => Promise<void>;

  // --- Slices ---
  getSlicesAction: () => Promise<SliceGroup[]>;
  getSliceAction: (sliceId: number | string) => Promise<SliceGroup>;
  createSliceAction: (data: SliceRequest) => Promise<SliceGroup>;
  updateSliceAction: (sliceId: number | string, data: SliceRequest) => Promise<SliceGroup>;
  duplicateSliceAction: (sliceId: number | string) => Promise<SliceGroup>;
  deleteSliceAction: (sliceId: number | string) => Promise<void>;

  // --- Planned deals ---
  getPlannedDealsAction: () => Promise<PlannedDeal[]>;
  createPlannedDealAction: (data: PlannedDealRequest) => Promise<PlannedDeal>;
  updatePlannedDealAction: (dealId: number | string, data: PlannedDealRequest) => Promise<PlannedDeal>;
  deletePlannedDealAction: (dealId: number | string) => Promise<void>;
  getPlannedDealAllocationPreviewAction: (data: AllocationPreviewRequest) => Promise<AllocationPreviewRow[]>;

  // --- Target policy ---
  getTargetPolicyAction: (sliceId: number | string, sliceType: "investors" | "slices") => Promise<TargetPolicyItem[]>;
  replaceTargetPolicyAction: (
    sliceId: number | string,
    sliceType: "investors" | "slices",
    policy: TargetPolicySaveRequest
  ) => Promise<TargetPolicyItem[]>;

  // --- Objectives (1.2.2 / 1.2.3) ---
  getObjectivesAction: (sliceId: number | string, sliceType: "investors" | "slices") => Promise<IObjectivesResponse>;
  saveObjectivesAction: (
    sliceId: number | string,
    sliceType: "investors" | "slices",
    body: IObjectivesSaveRequest
  ) => Promise<IObjectivesResponse>;
  getObjectiveDefaultsAction: () => Promise<IObjectiveDefaults>;
  updateObjectiveDefaultsAction: (body: IObjectiveDefaultsSaveRequest) => Promise<IObjectiveDefaults>;
  getObjectiveSourcesAction: (constraintKey: ObjectiveConstraintKey) => Promise<IObjectiveSource[]>;
}

export class PortfolioConstructionActions extends AsyncActions implements IPortfolioConstructionActions {
  public constructor(params: AsyncActionsParams) {
    super(params);
  }

  public getCorrelationDataAction: IPortfolioConstructionActions["getCorrelationDataAction"] = async ({ ...body }) => {
    const {
      data: { data }
    } = await this.api.post<AltiusRes<IPortfolioConstructionCorrelationData>>({
      url: "/api/v0.0.3/portfolio-construction/investors/correlation-matrix",
      data: body
    });
    return data;
  };

  public getDealTreeAction: IPortfolioConstructionActions["getDealTreeAction"] = async (investorId) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IPortfolioConstructionDealTreeItem[]>>({
      url: "/api/v0.0.3/portfolio-construction/investors/deal-tree",
      ...(investorId && { params: { investor_id: investorId } })
    });
    return data;
  };

  public getDisplayModeAction: IPortfolioConstructionActions["getDisplayModeAction"] = async (
    sliceId,
    sliceType,
    currency
  ) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<DisplayModeResponse>>({
      url: "/api/v0.0.3/portfolio-construction/display-mode",
      params: {
        slice_id: sliceId,
        slice_type: sliceType,
        ...(currency ? { currency } : {})
      }
    });
    return data;
  };

  public setCorrelationMatrixSettingsAction: IPortfolioConstructionActions["setCorrelationMatrixSettingsAction"] =
    async (data) => {
      this.dispatch({
        type: ReportsAction.SetCorrelationMatrixSettings,
        payload: data
      });
    };

  // --------------------------------- new endpoints ---------------------------------

  public getSlicesAction: IPortfolioConstructionActions["getSlicesAction"] = async () => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<SliceGroup[]>>({
      url: "/api/v0.0.3/portfolio-construction/slices"
    });
    return data;
  };

  public getSliceAction: IPortfolioConstructionActions["getSliceAction"] = async (sliceId) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<SliceGroup>>({
      url: `/api/v0.0.3/portfolio-construction/slices/${sliceId}`
    });
    return data;
  };

  public createSliceAction: IPortfolioConstructionActions["createSliceAction"] = async (body) => {
    const {
      data: { data }
    } = await this.api.post<AltiusRes<SliceGroup>>({
      url: "/api/v0.0.3/portfolio-construction/slices",
      data: body
    });
    return data;
  };

  public updateSliceAction: IPortfolioConstructionActions["updateSliceAction"] = async (sliceId, body) => {
    const {
      data: { data }
    } = await this.api.patch<AltiusRes<SliceGroup>>({
      url: `/api/v0.0.3/portfolio-construction/slices/${sliceId}`,
      data: body
    });
    return data;
  };

  public duplicateSliceAction: IPortfolioConstructionActions["duplicateSliceAction"] = async (sliceId) => {
    const {
      data: { data }
    } = await this.api.post<AltiusRes<SliceGroup>>({
      url: `/api/v0.0.3/portfolio-construction/slices/${sliceId}/duplicate`
    });
    return data;
  };

  public deleteSliceAction: IPortfolioConstructionActions["deleteSliceAction"] = async (sliceId) => {
    await this.api.delete<AltiusRes<void>>({
      url: `/api/v0.0.3/portfolio-construction/slices/${sliceId}`
    });
  };

  public getPlannedDealsAction: IPortfolioConstructionActions["getPlannedDealsAction"] = async () => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<PlannedDealApi[]>>({
      url: "/api/v0.0.3/portfolio-construction/planned-deals"
    });
    return data.map(mapPlannedDeal);
  };

  public createPlannedDealAction: IPortfolioConstructionActions["createPlannedDealAction"] = async (body) => {
    const {
      data: { data }
    } = await this.api.post<AltiusRes<PlannedDealApi>>({
      url: "/api/v0.0.3/portfolio-construction/planned-deals",
      data: toPlannedDealBody(body)
    });
    return mapPlannedDeal(data);
  };

  public updatePlannedDealAction: IPortfolioConstructionActions["updatePlannedDealAction"] = async (dealId, body) => {
    const {
      data: { data }
    } = await this.api.patch<AltiusRes<PlannedDealApi>>({
      url: `/api/v0.0.3/portfolio-construction/planned-deals/${dealId}`,
      data: toPlannedDealBody(body)
    });
    return mapPlannedDeal(data);
  };

  public deletePlannedDealAction: IPortfolioConstructionActions["deletePlannedDealAction"] = async (dealId) => {
    await this.api.delete<AltiusRes<void>>({
      url: `/api/v0.0.3/portfolio-construction/planned-deals/${dealId}`
    });
  };

  public getPlannedDealAllocationPreviewAction: IPortfolioConstructionActions["getPlannedDealAllocationPreviewAction"] =
    async ({ amount, currency, investors }) => {
      const {
        data: { data }
      } = await this.api.post<AltiusRes<AllocationPreviewApiResponse>>({
        url: "/api/v0.0.3/portfolio-construction/planned-deals/allocation-preview",
        data: { amount: String(amount), currency, investors }
      });
      return data.map(
        (row): AllocationPreviewRow => ({
          investor_id: row.investor_id,
          investor_name: row.investor_name,
          suggested_amount: toMoneyNumber(row.suggested_amount),
          excluded_no_nav: Boolean(row.excluded_no_nav)
        })
      );
    };

  public getTargetPolicyAction: IPortfolioConstructionActions["getTargetPolicyAction"] = async (sliceId, sliceType) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<TargetPolicyItem[]>>({
      url: `/api/v0.0.3/portfolio-construction/${sliceType}/${sliceId}/target-policy`
    });
    return data;
  };

  public replaceTargetPolicyAction: IPortfolioConstructionActions["replaceTargetPolicyAction"] = async (
    sliceId,
    sliceType,
    policy
  ) => {
    const {
      data: { data }
    } = await this.api.post<AltiusRes<TargetPolicyItem[]>>({
      url: `/api/v0.0.3/portfolio-construction/${sliceType}/${sliceId}/target-policy`,
      data: { items: policy }
    });
    return data;
  };

  // --------------------------------- objectives (1.2.2 / 1.2.3) ---------------------------------

  public getObjectivesAction: IPortfolioConstructionActions["getObjectivesAction"] = async (sliceId, sliceType) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IObjectivesResponse>>({
      url: `/api/v0.0.3/portfolio-construction/${sliceType}/${sliceId}/objectives`
    });
    return data;
  };

  public saveObjectivesAction: IPortfolioConstructionActions["saveObjectivesAction"] = async (
    sliceId,
    sliceType,
    body
  ) => {
    const {
      data: { data }
    } = await this.api.post<AltiusRes<IObjectivesResponse>>({
      url: `/api/v0.0.3/portfolio-construction/${sliceType}/${sliceId}/objectives`,
      data: body
    });
    return data;
  };

  public getObjectiveDefaultsAction: IPortfolioConstructionActions["getObjectiveDefaultsAction"] = async () => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IObjectiveDefaults>>({
      url: "/api/v0.0.3/portfolio-construction/objective-defaults"
    });
    return data;
  };

  public updateObjectiveDefaultsAction: IPortfolioConstructionActions["updateObjectiveDefaultsAction"] = async (
    body
  ) => {
    const {
      data: { data }
    } = await this.api.put<AltiusRes<IObjectiveDefaults>>({
      url: "/api/v0.0.3/portfolio-construction/objective-defaults",
      data: body
    });
    return data;
  };

  public getObjectiveSourcesAction: IPortfolioConstructionActions["getObjectiveSourcesAction"] = async (
    constraintKey
  ) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IObjectiveSource[]>>({
      url: `/api/v0.0.3/portfolio-construction/objective-sources/${constraintKey}`
    });
    return data;
  };

  // --------------------------------- pie chart data ---------------------------------
  public getPieChartDataAction: IPortfolioConstructionActions["getPieChartDataAction"] = async (sliceId, sliceType) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IPortfolioConstructionPieChart[]>>({
      url: `/api/v0.0.3/portfolio-construction/${sliceType}/${sliceId}/pie-chart`
    });
    return data;
  };

  public getCompareGraphDataAction: IPortfolioConstructionActions["getCompareGraphDataAction"] = async (
    sliceId,
    sliceType
  ) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IPortfolioConstructionCompareGraph>>({
      url: `/api/v0.0.3/portfolio-construction/${sliceType}/${sliceId}/column-chart`
    });
    return data;
  };
}
